import { useQuery } from '@tanstack/react-query';
import type { AtendenteIaConfig, LeadsAutomaticos, Papel } from '@erp/shared';
import { api } from './api';

export interface Pessoa {
  id: string;
  nome: string;
  papel: Papel;
  filialIds: string[];
}
export interface Etapa {
  id: string;
  nome: string;
  tipo: 'aberta' | 'ganho' | 'perdido';
  ordem: number;
}
export interface LeadItem {
  id: string;
  clienteId: string | null;
  nome: string;
  telefone: string;
  email: string | null;
  mensagem: string | null;
  origem: string;
  dados: Record<string, unknown> | null;
  status: 'novo' | 'em_atendimento' | 'convertido' | 'perdido';
  etapa: { id: string; nome: string; tipo: Etapa['tipo'] } | null;
  vendedorId: string | null;
  motivoPerda: string | null;
  semResposta: boolean;
  /** Passou do prazo de resposta da loja sem resposta */
  atrasado?: boolean;
  /** Foi para outro vendedor por falta de resposta */
  redistribuido?: boolean;
  propostas: number;
  criadoEm: string;
  atualizadoEm: string;
  veiculo: { id: string; descricao: string; precoCentavos: number | null } | null;
}

/** Pessoas ativas da loja (para escolher vendedor e transferir). */
export function usePessoas() {
  return useQuery({
    queryKey: ['equipe', 'pessoas'],
    queryFn: () => api<Pessoa[]>('/equipe/pessoas'),
    staleTime: 300_000,
  });
}

export function useFunil() {
  return useQuery({
    queryKey: ['funil'],
    queryFn: () =>
      api<{
        etapas: Etapa[];
        config: {
          limiteDescontoPercentual: number;
          distribuicao: 'rodizio' | 'manual';
          prazoRespostaMinutos?: number;
          redistribuirAtrasados?: boolean;
          leadsAutomaticos?: LeadsAutomaticos;
          atendenteIa?: AtendenteIaConfig;
        };
      }>('/funil'),
    staleTime: 300_000,
  });
}

export const nomePessoa = (pessoas: Pessoa[] | undefined, id: string | null | undefined) =>
  id ? (pessoas?.find((p) => p.id === id)?.nome ?? '—') : 'Sem vendedor';

/** Link do WhatsApp (abre o app no celular) com mensagem inicial opcional. */
export const linkWhatsapp = (telefone: string | null | undefined, texto?: string) => {
  const d = (telefone ?? '').replace(/\D/g, '');
  if (!d) return null;
  const num = d.length <= 11 ? `55${d}` : d;
  return `https://wa.me/${num}${texto ? `?text=${encodeURIComponent(texto)}` : ''}`;
};

export const telefoneLegivel = (t: string | null | undefined) => {
  const d = (t ?? '').replace(/\D/g, '').replace(/^55(?=\d{10,11}$)/, '');
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return t ?? '';
};

export const tempoRelativo = (iso: string) => {
  const min = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min} min`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h} h`;
  const d = Math.round(h / 24);
  return d === 1 ? 'ontem' : `${d} dias`;
};

/** Rótulos de status usados nas fichas do cliente e do carro. */
export const STATUS_PROPOSTA: Record<string, string> = {
  aberta: 'Aberta',
  aguardando_aprovacao: 'Esperando aprovação',
  aprovada: 'Desconto aprovado',
  recusada: 'Recusada',
  aceita: 'Fechada',
  cancelada: 'Cancelada',
};
export const STATUS_TD: Record<string, string> = {
  agendado: 'Agendado',
  realizado: 'Realizado',
  cancelado: 'Cancelado',
  nao_compareceu: 'Não veio',
};

/** Nomes dos eventos do histórico do cliente. */
export const EVENTO: Record<string, string> = {
  lead_criado: 'Lead criado',
  lead_atualizado: 'Lead atualizado',
  proposta_criada: 'Proposta feita',
  test_drive_agendado: 'Test drive agendado',
  test_drive_realizado: 'Test drive realizado',
  troca_avaliada: 'Troca avaliada',
  desejo_cadastrado: 'Desejo cadastrado',
  desejo_atendido: 'Carro desejado chegou',
  cliente_atualizado: 'Dados atualizados',
};
